import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  ClipboardCheck, Plus, ArrowLeft, Search, RefreshCw, FileSpreadsheet, Lock, Trash2, Check,
  Package, Pill, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Wand2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/datetime';
import { downloadPdf } from '../utils/downloadPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const can = (user, action, scope) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const mod = scope === 'area' ? 'conteo_fisico_area' : 'conteo_fisico';
  return user.permissions?.[mod]?.[action] === true;
};

export default function StockCounts({ scope = 'warehouse' }) {
  const isArea = scope === 'area';
  const { user } = useOutletContext();
  const [view, setView] = useState('list');
  const [counts, setCounts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newWh, setNewWh] = useState('');
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Ajuste automático de diferencias
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustChecks, setAdjustChecks] = useState({});
  const [adjustReason, setAdjustReason] = useState('');
  const [lotOptions, setLotOptions] = useState({});
  const [lotForm, setLotForm] = useState({});
  const [pendingLots, setPendingLots] = useState([]);
  const [lotFix, setLotFix] = useState({});
  const [adjusting, setAdjusting] = useState(false);

  const canCreate = can(user, 'crear', scope);
  const canFinalize = can(user, 'finalizar', scope);
  const canDelete = can(user, 'eliminar', scope);
  // El ajuste de stock real nunca se hace desde el área: lo ejecuta un supervisor/admin
  // desde Stock y Movimientos → Conteo Físico (control cruzado).
  const canAdjust = !isArea && can(user, 'ajustar', scope);

  const loadList = async () => {
    setLoading(true);
    try {
      const c = await axios.get(`${API}/stock-counts${isArea ? '?scope=area' : ''}`, { withCredentials: true });
      setCounts(c.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los conteos');
    }
    // El listado no depende del selector: si no tiene permiso de Depósitos, igual ve sus conteos.
    try {
      const w = await axios.get(isArea ? `${API}/stock-counts/my-areas` : `${API}/warehouses`, { withCredentials: true });
      setWarehouses(w.data || []);
    } catch { setWarehouses([]); }
    try {
      if (!isArea && can(user, 'ajustar')) {
        const p = await axios.get(`${API}/stock-adjustments/lots-without-expiry`, { withCredentials: true });
        setPendingLots(p.data || []);
      }
    } catch { setPendingLots([]); }
    setLoading(false);
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line

  const openDetail = async (countId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock-counts/${countId}`, { withCredentials: true });
      setDetail(r.data);
      const initial = {};
      (r.data.items || []).forEach(it => {
        if (it.counted_qty !== null && it.counted_qty !== undefined) initial[it.article_id] = String(it.counted_qty);
      });
      setEdits(initial);
      setSearch('');
      setOnlyDiff(false);
      setView('detail');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo abrir el conteo');
    } finally {
      setLoading(false);
    }
  };

  const createCount = async () => {
    if (!newWh) { toast.error(isArea ? 'Elegí el área a arquear' : 'Elegí un depósito'); return; }
    try {
      const body = isArea ? { area_id: newWh } : { warehouse_id: newWh };
      const r = await axios.post(`${API}/stock-counts`, body, { withCredentials: true });
      setShowNew(false);
      setNewWh('');
      toast.success(isArea ? 'Arqueo abierto' : 'Conteo abierto');
      await openDetail(r.data.count_id);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear el conteo');
    }
  };

  const saveCounts = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = {};
      Object.entries(edits).forEach(([aid, val]) => {
        if (val !== '' && val !== null && val !== undefined && !isNaN(Number(val))) payload[aid] = Number(val);
      });
      await axios.put(`${API}/stock-counts/${detail.count_id}`, { counts: payload }, { withCredentials: true });
      toast.success('Cantidades guardadas');
      await openDetail(detail.count_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (!detail) return;
    try {
      // Guardar antes de finalizar para no perder cambios cargados
      const payload = {};
      Object.entries(edits).forEach(([aid, val]) => {
        if (val !== '' && !isNaN(Number(val))) payload[aid] = Number(val);
      });
      await axios.put(`${API}/stock-counts/${detail.count_id}`, { counts: payload }, { withCredentials: true });
      await axios.post(`${API}/stock-counts/${detail.count_id}/finalize`, {}, { withCredentials: true });
      toast.success('Conteo finalizado');
      setConfirmFinalize(false);
      await openDetail(detail.count_id);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo finalizar');
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/stock-counts/${deleteTarget.count_id}`, { withCredentials: true });
      toast.success('Conteo eliminado');
      setDeleteTarget(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const exportXlsx = async (diffOnly) => {
    if (!detail) return;
    try {
      const r = await axios.get(`${API}/stock-counts/${detail.count_id}/export?only_diff=${diffOnly}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `conteo_${detail.warehouse_name || 'deposito'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    }
  };

  const isDraft = detail?.status === 'draft';

  // Renglones con diferencia pendientes de ajuste (solo conteos finalizados con la función habilitada)
  const pendingAdjustRows = useMemo(() => {
    if (!detail || detail.status !== 'finalized' || !detail.adjustable) return [];
    return (detail.items || []).filter(it => it.difference !== null && it.difference !== undefined && it.difference !== 0 && !it.adjusted);
  }, [detail]);

  const openAdjust = async () => {
    const checks = {};
    pendingAdjustRows.forEach(r => { checks[r.article_id] = true; });
    setAdjustChecks(checks);
    setAdjustReason('');
    setLotForm({});
    try {
      const r = await axios.get(`${API}/stock-counts/${detail.count_id}/lot-options`, { withCredentials: true });
      setLotOptions(r.data || {});
    } catch { setLotOptions({}); }
    setShowAdjust(true);
  };

  const doAdjust = async () => {
    const selected = Object.entries(adjustChecks).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) { toast.error('Seleccioná al menos un artículo a ajustar'); return; }
    if (!adjustReason.trim()) { toast.error('El motivo del ajuste es obligatorio'); return; }
    const lots = {};
    for (const r of pendingAdjustRows) {
      if (!selected.includes(r.article_id) || r.difference <= 0) continue;
      const opt = lotOptions[r.article_id] || {};
      const sel = lotForm[r.article_id];
      if (!sel?.mode || (sel.mode === 'existing' && !sel.lot_id)) {
        toast.error(`Indicá a qué lote entra el sobrante de "${r.name}"`);
        return;
      }
      if (opt.is_medicamento && sel.mode === 'new' && (!sel.lot_number?.trim() || !sel.expiration_date)) {
        toast.error(`"${r.name}" es medicamento: informá lote y vencimiento`);
        return;
      }
      lots[r.article_id] = sel.mode === 'existing'
        ? { mode: 'existing', lot_id: sel.lot_id }
        : { mode: 'new', lot_number: (sel.lot_number || '').trim(), expiration_date: sel.expiration_date || '' };
    }
    setAdjusting(true);
    try {
      const r = await axios.post(`${API}/stock-counts/${detail.count_id}/adjust`,
        { article_ids: selected, reason: adjustReason.trim(), lots }, { withCredentials: true });
      toast.success(`Ajuste ${r.data.adjustment?.adjustment_number} generado (${selected.length} artículo/s)`);
      (r.data.warnings || []).forEach(w => toast.warning(w, { duration: 10000 }));
      setShowAdjust(false);
      await openDetail(detail.count_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar el ajuste');
    } finally {
      setAdjusting(false);
    }
  };

  const saveLotFix = async (lot) => {
    const f = lotFix[lot.lot_id] || {};
    try {
      await axios.put(`${API}/stock-adjustments/lots/${lot.lot_id}`, {
        lot_number: f.lot_number ?? lot.lot_number,
        expiration_date: f.expiration_date || '',
      }, { withCredentials: true });
      toast.success('Lote actualizado');
      setPendingLots(prev => prev.filter(l => l.lot_id !== lot.lot_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar el lote');
    }
  };

  const rows = useMemo(() => {
    if (!detail?.items) return [];
    return detail.items.map(it => {
      const raw = edits[it.article_id];
      const countedLive = (raw === '' || raw === undefined || raw === null || isNaN(Number(raw))) ? null : Number(raw);
      const useCounted = isDraft ? countedLive : it.counted_qty;
      const diff = useCounted === null || useCounted === undefined ? null : useCounted - Number(it.system_qty);
      return { ...it, _counted: useCounted, _diff: diff };
    }).filter(it => {
      if (search && !((it.name || '').toLowerCase().includes(search.toLowerCase()))) return false;
      if (onlyDiff && (it._diff === null || it._diff === 0)) return false;
      return true;
    });
  }, [detail, edits, search, onlyDiff, isDraft]);

  // Artículos que figuran con más de una ficha en el mismo depósito (base + espejo del
  // subdepósito). Se avisa para que no se cuente dos veces el mismo estante.
  const dupNames = useMemo(() => {
    const seen = {};
    (detail?.items || []).forEach(it => { seen[it.name] = (seen[it.name] || 0) + 1; });
    return seen;
  }, [detail]);

  const liveSummary = useMemo(() => {
    if (!detail?.items) return { total: 0, counted: 0, with_difference: 0, not_counted: 0 };
    let counted = 0, withDiff = 0;
    detail.items.forEach(it => {
      const raw = edits[it.article_id];
      const c = isDraft
        ? ((raw === '' || raw === undefined || raw === null || isNaN(Number(raw))) ? null : Number(raw))
        : it.counted_qty;
      if (c !== null && c !== undefined) {
        counted++;
        if (c - Number(it.system_qty) !== 0) withDiff++;
      }
    });
    const total = detail.items.length;
    return { total, counted, not_counted: total - counted, with_difference: withDiff };
  }, [detail, edits, isDraft]);

  // ============================ LIST VIEW ============================
  if (view === 'list') {
    return (
      <div className="p-6 space-y-6" data-testid="stock-counts-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-amber-600" />
              {isArea ? 'Arqueo del Área' : 'Conteo Físico de Stock'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {isArea
                ? 'Contá el stock real de tu subdepósito y compará contra el sistema. Reporta diferencias: el ajuste lo hace luego un supervisor.'
                : 'Cargá lo contado en cada depósito y compará contra el sistema. Reporta diferencias (no ajusta stock).'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadList} disabled={loading} data-testid="refresh-counts">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </Button>
            {canCreate && (
              <Button onClick={() => setShowNew(true)} data-testid="new-count-btn">
                <Plus className="w-4 h-4 mr-2" /> {isArea ? 'Nuevo arqueo' : 'Nuevo conteo'}
              </Button>
            )}
          </div>
        </div>

        {pendingLots.length > 0 && (
          <Card className="border-amber-300" data-testid="pending-lots-card">
            <CardHeader>
              <CardTitle className="text-base">Lotes de ajustes anteriores sin vencimiento ({pendingLots.length})</CardTitle>
              <CardDescription>
                Sobrantes que se ingresaron antes de pedir estos datos. Sin vencimiento no entran en las alertas
                y el FEFO los deja para el final, así que conviene completarlos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingLots.map(lot => {
                const f = lotFix[lot.lot_id] || {};
                return (
                  <div key={lot.lot_id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center" data-testid={`pending-lot-${lot.lot_id}`}>
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="font-medium">
                        {lot.article_name}
                        {lot.is_medicamento && <Badge variant="outline" className="ml-2 text-[10px] border-rose-300 text-rose-700">medicamento</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {lot.warehouse_name} · {lot.quantity_remaining} {lot.unit} · ingresado por el ajuste {lot.from_adjustment}
                      </p>
                    </div>
                    <Input
                      className="h-9 sm:w-40"
                      placeholder="Nro. de lote"
                      value={f.lot_number ?? (lot.lot_number || '')}
                      onChange={(e) => setLotFix(prev => ({ ...prev, [lot.lot_id]: { ...f, lot_number: e.target.value } }))}
                      data-testid={`fix-lot-number-${lot.lot_id}`}
                    />
                    <Input
                      type="date"
                      className="h-9 sm:w-44"
                      value={f.expiration_date || ''}
                      onChange={(e) => setLotFix(prev => ({ ...prev, [lot.lot_id]: { ...f, expiration_date: e.target.value } }))}
                      data-testid={`fix-lot-exp-${lot.lot_id}`}
                    />
                    <Button size="sm" onClick={() => saveLotFix(lot)} data-testid={`fix-lot-save-${lot.lot_id}`}>Guardar</Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Conteos</CardTitle>
            <CardDescription>{counts.length} conteo(s)</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <p className="text-muted-foreground p-6">Cargando…</p>
            ) : counts.length === 0 ? (
              <p className="text-muted-foreground p-6">Todavía no hay conteos. Creá uno con "Nuevo conteo".</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isArea ? 'Área' : 'Depósito'}</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Creado por</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {counts.map(c => (
                      <TableRow key={c.count_id} className="hover:bg-accent cursor-pointer" onClick={() => openDetail(c.count_id)} data-testid={`count-row-${c.count_id}`}>
                        <TableCell className="font-medium">
                          {c.warehouse_name}
                          {!isArea && c.area_name && (
                            <Badge variant="outline" className="ml-2 text-[10px]" data-testid={`count-area-badge-${c.count_id}`}>
                              Arqueo del área {c.area_name}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const adjCount = (c.adjustments || []).length;
                            const pend = c.unadjusted_diff_count || 0;
                            const pendBadge = pend > 0 ? (
                              <Badge
                                className="ml-1 bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 whitespace-nowrap"
                                title={`${pend} artículo(s) con diferencia que todavía no fueron ajustados. Abrí el arqueo y ajustá los pendientes.`}
                                data-testid={`count-unadjusted-${c.count_id}`}
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {pend} sin ajustar
                              </Badge>
                            ) : null;
                            if (c.status !== 'finalized') {
                              return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" data-testid={`count-status-${c.count_id}`}>Borrador</Badge>;
                            }
                            if (adjCount > 0) {
                              return (
                                <span className="inline-flex items-center flex-wrap gap-1">
                                  <Badge className="bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200" title={`${adjCount} ajuste${adjCount === 1 ? '' : 's'} aplicado${adjCount === 1 ? '' : 's'}`} data-testid={`count-status-${c.count_id}`}>
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Ajustado{adjCount > 1 ? ` (${adjCount})` : ''}
                                  </Badge>
                                  {pendBadge}
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center flex-wrap gap-1">
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid={`count-status-${c.count_id}`}><Lock className="w-3 h-3 mr-1" />Finalizado</Badge>
                                {pendBadge}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{c.created_by_name || '—'}</TableCell>
                        <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => openDetail(c.count_id)} data-testid={`open-count-${c.count_id}`}>Abrir</Button>
                          {canDelete && (() => {
                            const adj = (c.adjustments || []).map(a => a.adjustment_number).filter(Boolean);
                            const blocked = adj.length > 0 && user?.role !== 'admin';
                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                disabled={blocked}
                                title={blocked
                                  ? `Ya generó el ajuste ${adj.join(', ')}: no se puede borrar el respaldo del movimiento de stock`
                                  : 'Eliminar la hoja de conteo (no revierte stock)'}
                                onClick={() => setDeleteTarget(c)}
                                data-testid={`delete-count-${c.count_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nuevo conteo */}
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isArea ? 'Nuevo arqueo del área' : 'Nuevo conteo físico'}</DialogTitle>
              <DialogDescription>
                {isArea
                  ? 'Elegí el área a arquear. Se toma una foto del stock actual de su subdepósito para comparar.'
                  : 'Elegí el depósito a contar. Se toma una foto del stock actual del sistema para comparar.'}
              </DialogDescription>
            </DialogHeader>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">{isArea ? 'Área' : 'Depósito'}</div>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={newWh}
                onChange={(e) => setNewWh(e.target.value)}
                data-testid="new-count-warehouse"
              >
                <option value="">{isArea ? 'Seleccioná un área…' : 'Seleccioná un depósito…'}</option>
                {isArea
                  ? warehouses.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)
                  : warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={createCount} data-testid="create-count-confirm">{isArea ? 'Abrir arqueo' : 'Abrir conteo'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmar borrado */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar conteo</DialogTitle>
              <DialogDescription>
                Se elimina la <b>hoja de conteo</b> de "{deleteTarget?.warehouse_name}"
                {deleteTarget?.area_name ? ` (arqueo del área ${deleteTarget.area_name})` : ''}.
                Esto <b>no revierte el stock</b> ni los movimientos ya generados, y no se puede deshacer.
                {(deleteTarget?.adjustments || []).length > 0 && (
                  <span className="mt-2 block rounded-md bg-red-50 px-2 py-1 text-red-800 dark:bg-red-950/30 dark:text-red-200" data-testid="delete-count-adjusted-warning">
                    Atención: este conteo ya generó el ajuste{' '}
                    <b>{(deleteTarget.adjustments || []).map(a => a.adjustment_number).join(', ')}</b>.
                    El movimiento de stock queda, pero pierde el respaldo que lo justifica.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="confirm-delete-count">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============================ DETAIL VIEW ============================
  return (
    <div className="p-6 space-y-6" data-testid="stock-count-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setDetail(null); loadList(); }} data-testid="back-to-counts">
            <ArrowLeft className="w-4 h-4 mr-1" /> {isArea ? 'Arqueos' : 'Conteos'}
          </Button>
          <h1 className="text-2xl font-bold tracking-tight mt-2 flex items-center gap-2">
            {detail?.area_name || detail?.warehouse_name}
            {detail?.status === 'finalized'
              ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"><Lock className="w-3 h-3 mr-1" />Finalizado</Badge>
              : <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">Borrador</Badge>}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => exportXlsx(false)} data-testid="export-all-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportXlsx(true)} data-testid="export-diff-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Solo diferencias
          </Button>
          {isDraft && can(user, 'crear', scope) && (
            <Button variant="outline" onClick={saveCounts} disabled={saving} data-testid="save-count-btn">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          )}
          {isDraft && canFinalize && (
            <Button onClick={() => setConfirmFinalize(true)} data-testid="finalize-count-btn">
              <Check className="w-4 h-4 mr-2" /> Finalizar
            </Button>
          )}
          {canAdjust && pendingAdjustRows.length > 0 && (
            <Button onClick={openAdjust} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-adjust-btn">
              <Wand2 className="w-4 h-4 mr-2" /> Generar Ajuste de Stock ({pendingAdjustRows.length})
            </Button>
          )}
        </div>
      </div>

      {/* Ajustes ya generados desde este conteo */}
      {(detail?.adjustments || []).length > 0 && (
        <Card className="border-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-950/20" data-testid="adjustments-card">
          <CardContent className="pt-4 pb-4 space-y-2">
            {(detail.adjustments || []).map((a, i) => (
              <div key={a.adjustment_id || i} className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <b>{a.adjustment_number}</b> · {a.items} artículo(s) ajustado(s) por {a.by_name} el {fmtDate(a.at)}
                </span>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(`/stock-adjustments/${a.adjustment_id}/pdf`, a.adjustment_number)} data-testid={`adjustment-pdf-${a.adjustment_id}`}>
                  <FileText className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Artículos" value={liveSummary.total} icon={<Package className="w-4 h-4 text-muted-foreground" />} />
        <SummaryCard label="Contados" value={liveSummary.counted} color="bg-sky-50 dark:bg-sky-950/30 border-sky-300/50" icon={<CheckCircle2 className="w-4 h-4 text-sky-600" />} />
        <SummaryCard label="Sin contar" value={liveSummary.not_counted} color="bg-slate-50 dark:bg-slate-900/40" />
        <SummaryCard label="Con diferencia" value={liveSummary.with_difference} color="bg-amber-50 dark:bg-amber-950/30 border-amber-300/50" icon={<AlertTriangle className="w-4 h-4 text-amber-600" />} />
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar artículo…" className="pl-8" data-testid="count-search" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid="only-diff-toggle" />
            Solo con diferencia
          </label>
        </CardContent>
      </Card>

      {/* Tabla de conteo */}
      <Card>
        <CardHeader>
          <CardTitle>Planilla de conteo</CardTitle>
          <CardDescription>
            {isDraft ? 'Cargá la cantidad contada de cada artículo. La diferencia se calcula sola.' : 'Conteo finalizado (solo lectura).'} · {rows.length} fila(s)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-muted-foreground p-6">Cargando…</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground p-6">No hay artículos con esos filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artículo</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-right">Stock Sistema</TableHead>
                    <TableHead className="text-right w-[140px]">Contado</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(it => {
                    const diff = it._diff;
                    const diffTone = diff === null ? 'text-muted-foreground'
                      : diff > 0 ? 'text-emerald-600 font-semibold'
                      : diff < 0 ? 'text-red-600 font-semibold'
                      : 'text-muted-foreground';
                    return (
                      <TableRow key={it.article_id} data-testid={`count-item-${it.article_id}`}>
                        <TableCell>
                          <span className="font-medium">{it.name}</span>
                          {it.is_external && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-[10px] border-sky-300 text-sky-700"
                              title="Medicación traída por familiares (REM): es del residente, no del stock del hogar."
                              data-testid={`external-badge-${it.article_id}`}
                            >
                              de familiares
                            </Badge>
                          )}
                          {dupNames[it.name] > 1 && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-[10px] border-amber-300 text-amber-700"
                              title="Este artículo tiene más de una ficha en este depósito: cargá en cada renglón lo que corresponde a esa ficha."
                              data-testid={`dup-name-badge-${it.article_id}`}
                            >
                              {dupNames[it.name]} fichas
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{it.category || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {it.system_qty} <span className="text-xs text-muted-foreground">{it.unit}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {isDraft ? (
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              className="h-8 w-28 text-right ml-auto"
                              value={edits[it.article_id] ?? ''}
                              placeholder="—"
                              onChange={(e) => {
                                const raw = e.target.value;
                                // v3.27.3 — bloquear valores negativos: son imposibles físicamente
                                if (raw !== '' && Number(raw) < 0) return;
                                setEdits(prev => ({ ...prev, [it.article_id]: raw }));
                              }}
                              data-testid={`count-input-${it.article_id}`}
                            />
                          ) : (
                            <span className="tabular-nums">{it._counted === null || it._counted === undefined ? '—' : it._counted}</span>
                          )}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums ${diffTone}`} data-testid={`count-diff-${it.article_id}`}>
                          {diff === null ? '—' : (
                            <span className="inline-flex items-center gap-1 justify-end">
                              {diff > 0 && <TrendingUp className="w-3 h-3" />}
                              {diff < 0 && <TrendingDown className="w-3 h-3" />}
                              {diff > 0 ? `+${diff}` : diff}
                              {it.adjusted && (
                                <Badge className="ml-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 text-[10px] px-1.5" data-testid={`adjusted-badge-${it.article_id}`}>
                                  Ajustado
                                </Badge>
                              )}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmar finalizar */}
      <Dialog open={confirmFinalize} onOpenChange={setConfirmFinalize}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar conteo</DialogTitle>
            <DialogDescription>
              Se congela el reporte de diferencias y ya no se podrá editar. Finalizar NO mueve stock: después, un usuario con permiso podrá generar el ajuste con un clic. ¿Confirmás?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmFinalize(false)}>Cancelar</Button>
            <Button onClick={finalize} data-testid="confirm-finalize-count">Finalizar conteo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generar Ajuste de Stock */}
      <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Generar Ajuste de Stock</DialogTitle>
            <DialogDescription>
              Convierte las diferencias del conteo en movimientos reales: los <b>faltantes</b> salen de los lotes por <b>FEFO</b> (vence primero, sale primero) y en los <b>sobrantes</b> hay que indicar <b>a qué lote entran</b>. En medicamentos el lote y el vencimiento son obligatorios. Destildá lo que prefieras recontar.
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="text-right">Sistema</TableHead>
                  <TableHead className="text-right">Contado</TableHead>
                  <TableHead className="text-right">Diferencia</TableHead>
                  <TableHead>Movimiento / Lote</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingAdjustRows.map(r => {
                  const opt = lotOptions[r.article_id] || { is_medicamento: false, lots: [] };
                  const sel = lotForm[r.article_id] || { mode: opt.lots.length ? 'existing' : 'new' };
                  const setSel = (patch) => setLotForm(prev => ({ ...prev, [r.article_id]: { ...sel, ...patch } }));
                  return (
                  <TableRow key={r.article_id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={!!adjustChecks[r.article_id]}
                        onChange={(e) => setAdjustChecks(prev => ({ ...prev, [r.article_id]: e.target.checked }))}
                        data-testid={`adjust-check-${r.article_id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {r.name}
                      {opt.is_medicamento && r.difference > 0 && (
                        <Badge variant="outline" className="ml-2 text-[10px] border-rose-300 text-rose-700">medicamento</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.system_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.counted_qty}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${r.difference > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {r.difference > 0 ? `+${r.difference}` : r.difference}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.difference < 0 ? (
                        <span className="text-red-700">Salida por ajuste (FEFO: sale primero el que vence antes)</span>
                      ) : (
                        <div className="space-y-1 min-w-[280px]">
                          <select
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            value={sel.mode === 'new' ? '__new__' : (sel.lot_id || '')}
                            onChange={(e) => e.target.value === '__new__'
                              ? setSel({ mode: 'new', lot_id: '' })
                              : setSel({ mode: 'existing', lot_id: e.target.value })}
                            data-testid={`adjust-lot-select-${r.article_id}`}
                          >
                            <option value="">Elegí a qué lote entra…</option>
                            {opt.lots.map(lt => (
                              <option key={lt.lot_id} value={lt.lot_id}>
                                Sumar al lote {lt.lot_number || 's/n'} — vence {lt.expiration_date || 'sin fecha'} ({lt.quantity_remaining} u)
                              </option>
                            ))}
                            <option value="__new__">Lote nuevo…</option>
                          </select>
                          {sel.mode === 'new' && (
                            <div className="flex gap-1">
                              <Input
                                className="h-8 text-xs"
                                placeholder={opt.is_medicamento ? 'Nro. de lote *' : 'Nro. de lote (opcional)'}
                                value={sel.lot_number || ''}
                                onChange={(e) => setSel({ lot_number: e.target.value })}
                                data-testid={`adjust-lot-number-${r.article_id}`}
                              />
                              <Input
                                type="date"
                                className="h-8 text-xs"
                                value={sel.expiration_date || ''}
                                onChange={(e) => setSel({ expiration_date: e.target.value })}
                                data-testid={`adjust-lot-exp-${r.article_id}`}
                              />
                            </div>
                          )}
                          {opt.is_medicamento && sel.mode === 'new' && (!sel.lot_number || !sel.expiration_date) && (
                            <p className="text-[11px] text-amber-700">Al ser medicamento, el lote y el vencimiento son obligatorios.</p>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">Motivo del ajuste *</div>
            <Textarea
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              rows={2}
              placeholder="Ej: diferencias detectadas en conteo físico mensual…"
              data-testid="adjust-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjust(false)}>Cancelar</Button>
            <Button
              onClick={doAdjust}
              disabled={adjusting || !adjustReason.trim() || Object.values(adjustChecks).every(v => !v)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-adjust-btn"
            >
              {adjusting ? 'Generando…' : 'Confirmar Ajuste'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, color = 'bg-card', icon }) {
  return (
    <div className={`p-4 rounded-lg border ${color}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
